import breadcrumbComp from "../../common/breadcrumb-comp.vue";
import EncryptUtility from "../../utility/js/encrypt-utility";
import AddFavourite from "../../utility/js/addFav";
import salesService from "./sales-order-service.js";
import Utility from "../../../shared/utility";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import html2pdf from "html2pdf.js";
import moment from "moment";
import draggable from "vuedraggable";

const currentAsOfDate = () => moment().format("YYYY-MM-DD");
const DEFAULT_FILTERS = {
  project: [],
  business: [],
  facility: [],
  trendDays: 7,
  asOfDate: currentAsOfDate(),
};
const TREND_CHART = {
  left: 78,
  right: 980,
  top: 16,
  bottom: 170,
};
const DASHBOARD_GOOD_GREEN = "#16a34a";
const DEFAULT_DASHBOARD_LAYOUT = [
  { id: "orderVolume", title: "Order Volume Overview", visible: true, cols: 12, lg: 4 },
  { id: "atAGlance", title: "At A Glance", visible: true, cols: 12, lg: 4 },
  { id: "bto", title: "BTOs", visible: true, cols: 12, lg: 4 },
  { id: "shipmentAudits", title: "Shipment Audits", visible: true, cols: 12, lg: 4 },
  { id: "trend", title: "Orders Dropped Trend", visible: true, cols: 12, lg: 4 },
  { id: "orderStatus", title: "Order Status Breakdown", visible: true, cols: 12, lg: 4 },
];

export default {
  name: "salesOrderDashboard",
  components: {
    breadcrumbComp,
    draggable,
  },
  data() {
    return {
      userId: EncryptUtility.localStorageDecrypt("userID"),
      route: "",
      mainPage: "",
      subPage: "Sales Order Dashboard",
      pageUrl: "",
      backSlash: true,
      actionList: [],
      favouriteList: [],
      showAdd: false,
      asOfDateMenu: false,
      filters: { ...DEFAULT_FILTERS },
      projectList: [],
      businessList: [],
      facilityList: [],
      orderMatrixData: {},
      btoDashboardData: {},
      shipAuditDashboardData: {},
      btoDrillDialog: false,
      btoDrillTitle: "BTO Details",
      btoDrillNoDataText: "No BTO detail data available.",
      dashboardDrillType: "",
      btoDrillLoading: false,
      btoDrillRows: [],
      btoDrillHeaders: [],
      btoDrillSearch: "",
      btoDrillColumnFilters: {},
      dashboardDrillWindowHeight: typeof window !== "undefined" ? window.innerHeight : 800,
      orderMatrixLoaded: false,
      dashboardMetricRequestId: 0,
      dashboardLoading: {
        orderStatus: false,
        sla: false,
        bto: false,
        matrix: false,
        shipAudit: false,
        trend: false,
      },
      dashboardInsight: null,
      hoveredTrendIndex: null,
      hoveredTrendSeries: null,
      hasPendingDashboardFilterChanges: false,
      suppressNextDashboardFilterChange: false,
      isApplyingDashboardFilters: false,
      dashboardPendingApplyTimer: null,
      isDashboardCustomizing: false,
      isTrendExpanded: false,
      dashboardLayout: this.defaultDashboardLayout(),
      dashboardLayoutDraft: [],
      trendDaysList: [
        { text: "7 Days", value: 7 },
        { text: "30 Days", value: 30 },
        { text: "60 Days", value: 60 },
        { text: "90 Days", value: 90 },
      ],
    };
  },
  computed: {
    asOfDateFormatted() {
      return this.filters.asOfDate ? moment(this.filters.asOfDate).format("MM/DD/YYYY") : "";
    },
    asOfDatePayload() {
      return moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate).format("YYYY-MM-DD");
    },
    dashboardAsOfLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      return asOfDate.isSame(moment(), "day") ? "Today" : asOfDate.format("MM/DD/YYYY");
    },
    ordersDroppedAsOfLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Dropped Today";
      return `Orders Dropped ${asOfDate.format("MM/DD/YYYY")}`;
    },
    ordersDroppedPreviousLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Dropped Yesterday";
      return `Orders Dropped ${asOfDate.subtract(1, "day").format("MM/DD/YYYY")}`;
    },
    ordersDroppedTrendSuffix() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      return asOfDate.isSame(moment(), "day") ? "yesterday" : "from the previous day";
    },
    ordersShippedLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Orders Shipped Today";
      return `Orders Shipped ${asOfDate.format("MM/DD/YYYY")}`;
    },
    dailyCancelsLabel() {
      const asOfDate = moment(this.filters.asOfDate || DEFAULT_FILTERS.asOfDate);
      if (asOfDate.isSame(moment(), "day")) return "Cancellations Today";
      return `Cancellations ${asOfDate.format("MM/DD/YYYY")}`;
    },
    btoOutputLabel() {
      return "BTO Output";
    },
    trendDaysPayload() {
      return parseInt(this.filters.trendDays || DEFAULT_FILTERS.trendDays);
    },
    facilityIdPayload() {
      const facilityIds = this.normalizeSelectedIds(this.filters.facility);
      return JSON.stringify(facilityIds.length ? facilityIds : [0]);
    },
    projectKeyPayload() {
      const projectIds = this.normalizeSelectedIds(this.filters.project);
      return JSON.stringify(projectIds.length ? projectIds : [0]);
    },
    businessKeyPayload() {
      const businessIds = this.normalizeSelectedIds(this.filters.business);
      return JSON.stringify(businessIds.length ? businessIds : [0]);
    },
    matrixSummary() {
      return this.firstDefined([
        this.orderMatrixData?.summary,
        this.orderMatrixData?.Summary,
        this.orderMatrixData?.dashboardSummary,
        this.orderMatrixData?.DashboardSummary,
      ]) || {};
    },
    btoMetricsSummary() {
      return this.firstDefined([
        this.btoDashboardData?.summary,
        this.btoDashboardData?.Summary,
        this.btoDashboardData?.dashboardSummary,
        this.btoDashboardData?.DashboardSummary,
      ]) || {};
    },
    shipAuditCounts() {
      return this.firstArray(
        this.shipAuditDashboardData,
        ["auditCounts", "AuditCounts"],
        ["OrdersSentToAudit", "OrdersAudited", "OrdersShipped", "AuditVsShippedPercent"],
      )[0] || {};
    },
    ordersShippedValue() {
      const auditOrdersShipped = this.firstDefined([
        this.shipAuditCounts?.OrdersShipped,
        this.shipAuditCounts?.ordersShipped,
      ]);
      return auditOrdersShipped !== undefined
        ? this.normalizeNumber(auditOrdersShipped)
        : this.metricValue(this.matrixSummary, ["dailyShipmentCount", "DailyShipmentCount"]);
    },
    shipmentAuditCards() {
      return [
        {
          label: "Sent To Audit",
          value: this.metricValue(this.shipAuditCounts, ["OrdersSentToAudit", "ordersSentToAudit"]),
          icon: "mdi-clipboard-arrow-right-outline",
          color: "#2563eb",
          drillType: "SHIPAUDIT",
          drillParameter: "SENT_TO_AUDIT",
        },
        {
          label: "Audited",
          value: this.metricValue(this.shipAuditCounts, ["OrdersAudited", "ordersAudited"]),
          icon: "mdi-clipboard-check-outline",
          color: DASHBOARD_GOOD_GREEN,
          drillType: "SHIPAUDIT",
          drillParameter: "AUDITED",
        },
        {
          label: "Shipped",
          value: this.metricValue(this.shipAuditCounts, ["OrdersShipped", "ordersShipped"]),
          icon: "mdi-truck-check-outline",
          color: "#0891b2",
        },
        {
          label: "Audit vs Shipped",
          value: this.metricValue(this.shipAuditCounts, ["AuditVsShippedPercent", "auditVsShippedPercent"]),
          icon: "mdi-percent-outline",
          color: "#7c3aed",
          isPercent: true,
        },
      ];
    },
    shipmentAuditActivity() {
      return this.normalizeBreakdown(
        this.firstArray(this.shipAuditDashboardData, ["auditActivty", "auditActivity", "AuditActivty", "AuditActivity"], ["SAStatus", "TotalSAs"]),
        ["SAStatus", "saStatus", "status", "Status"],
        ["TotalSAs", "totalSAs", "count", "Count"],
      ).map((item, index) => ({
        ...item,
        color: this.shipmentAuditStatusColor(item.label, index),
        description: this.shipmentAuditStatusDescription(item.label),
        icon: this.shipmentAuditStatusIcon(item.label),
      }));
    },
    shipmentAuditBarRows() {
      return this.shipmentAuditActivity.map((item) => ({
        ...item,
        width: this.shipmentAuditBarMax ? (this.normalizeNumber(item.value) / this.shipmentAuditBarMax) * 100 : 0,
        percent: this.shipmentAuditActivityTotal ? (this.normalizeNumber(item.value) / this.shipmentAuditActivityTotal) * 100 : 0,
      }));
    },
    shipmentAuditBarMax() {
      return Math.max(...this.shipmentAuditActivity.map((item) => this.normalizeNumber(item.value)), 1);
    },
    shipmentAuditActivityTotal() {
      return this.shipmentAuditActivity.reduce((total, item) => total + item.value, 0);
    },
    metricCards() {
      return [
        {
          label: "Total Open Orders",
          value: this.metricValue(this.matrixSummary, ["totalOpenOrders", "TotalOpenOrders", "openOrders", "OpenOrders", "totalOpenOrderCount", "TotalOpenOrderCount"]),
          icon: "mdi-clipboard-list-outline",
          color: "#2563eb",
          trendIcon: this.directionIcon(this.totalOrderChangeDirection),
          trendLabel: this.totalOrderTrendLabel,
          trendClass: this.directionClass(this.totalOrderChangeDirection),
          trendSuffix: "vs yesterday",
          loadingSource: "orderStatus",
        },
        {
          label: this.ordersDroppedAsOfLabel,
          value: this.metricValue(this.matrixSummary, ["ordersToday", "OrdersToday", "ordersDroppedToday", "OrdersDroppedToday", "droppedToday", "DroppedToday"]),
          icon: "mdi-cart-arrow-down",
          color: DASHBOARD_GOOD_GREEN,
          trendIcon: this.directionIcon(this.changeDirection),
          trendLabel: this.directionLabel(this.changeDirection, this.orderDroppedPercentChange),
          trendClass: this.directionClass(this.changeDirection),
          trendSuffix: this.ordersDroppedTrendSuffix,
          loadingSource: "orderStatus",
          drillType: "DROPPED",
        },
        ...this.slaCategoryMetricCards,
        {
          label: this.ordersShippedLabel,
          value: this.ordersShippedValue,
          icon: "mdi-truck-check-outline",
          color: DASHBOARD_GOOD_GREEN,
          loadingSource: "shipAudit",
          drillType: "SHIPPED",
        },
        {
          label: "BTOs Completed",
          value: this.metricValue(this.btoMetricsSummary, ["totalClosed", "TotalClosed", "closedOrders", "ClosedOrders"]),
          icon: "mdi-package-variant-closed-check",
          color: DASHBOARD_GOOD_GREEN,
          loadingSource: "bto",
          subMetrics: [
            {
              label: "Total BTOs",
              value: this.metricValue(this.btoMetricsSummary, ["totalBTOs", "TotalBTOs", "totalBtos", "TotalBtos", "todayBTOs", "TodayBTOs", "todayBtos", "TodayBtos"]),
            },
            {
              label: "Canceled/DOA'd",
              value: this.metricValue(this.btoMetricsSummary, ["totalCanceled", "TotalCanceled", "totalCancelled", "TotalCancelled", "canceledOrders", "CanceledOrders", "cancelledOrders", "CancelledOrders"]),
            },
          ],
        },
        {
          label: this.dailyCancelsLabel,
          value: this.metricValue(this.matrixSummary, ["todayCancels", "TodayCancels"]),
          icon: "mdi-cancel",
          color: "#dc2626",
          loadingSource: "matrix",
          drillType: "CANCELED",
        },
      ];
    },
    orderVolumeCards() {
      return [
        this.metricCards.find((metric) => metric.label === "Total Open Orders"),
        this.metricCards.find((metric) => metric.label === this.ordersDroppedAsOfLabel),
        this.metricCards.find((metric) => metric.label === this.ordersShippedLabel),
        this.metricCards.find((metric) => metric.label === this.dailyCancelsLabel),
      ].filter(Boolean);
    },
    serviceLevelCards() {
      return [
        this.findSlaMetricCard(["ON-TIME", "ON TIME"]) || this.defaultSlaMetricCard("On-Time"),
        this.findSlaMetricCard(["MUST SHIP"]) || this.defaultSlaMetricCard("Must Ship"),
        this.findSlaMetricCard(["PAST-DUE", "PAST DUE"]) || this.defaultSlaMetricCard("Past-Due"),
        this.findSlaMetricCard(["NO TAT", "NO-TAT", "NOTAT"]) || this.defaultSlaMetricCard("No TAT"),
      ];
    },
    btoExceptionCards() {
      return [
        {
          label: this.btoOutputLabel,
          value: this.metricValue(this.btoMetricsSummary, ["totalBTOs", "TotalBTOs", "totalBtos", "TotalBtos", "todayBTOs", "TodayBTOs", "todayBtos", "TodayBtos"]),
          icon: "mdi-package-variant-closed-check",
          color: "#2563eb",
          loadingSource: "bto",
          drillParameter: "",
        },
        {
          label: "BTOs Completed",
          value: this.metricValue(this.btoMetricsSummary, ["totalClosed", "TotalClosed", "closedOrders", "ClosedOrders"]),
          icon: "mdi-check-circle-outline",
          color: DASHBOARD_GOOD_GREEN,
          loadingSource: "bto",
          drillParameter: "C",
        },
        {
          label: "BTOs Canceled / DOA'd",
          value: this.metricValue(this.btoMetricsSummary, ["totalCanceled", "TotalCanceled", "totalCancelled", "TotalCancelled", "canceledOrders", "CanceledOrders", "cancelledOrders", "CancelledOrders"]),
          icon: "mdi-close-circle-outline",
          color: "#dc2626",
          loadingSource: "bto",
          drillParameter: "X",
        },
      ];
    },
    btoStatusBreakdown() {
      return this.normalizeBreakdown(
        this.firstArray(this.btoDashboardData, ["btoStatusBreakdown", "BtoStatusBreakdown", "BTOStatusBreakdown"], ["status", "Status", "totalBTOs", "TotalBTOs"]),
        ["status", "Status", "label", "Label"],
        ["totalBTOs", "TotalBTOs", "totalBtos", "TotalBtos", "count", "Count", "value", "Value"],
      ).map((item, index) => ({
        ...item,
        color: this.statusBarColor(item.label, index),
      }));
    },
    btoStatusTotal() {
      return this.btoStatusBreakdown.reduce((total, item) => total + item.value, 0);
    },
    atAGlanceCards() {
      return this.serviceLevelCards.map((metric) => ({
        ...metric,
        description: this.glanceDescription(metric.label),
        tone: this.glanceTone(metric.label),
      }));
    },
    recentActivityItems() {
      const pastDue = this.findSlaMetricCard(["PAST-DUE", "PAST DUE"]);
      const mustShip = this.findSlaMetricCard(["MUST SHIP"]);
      const shippedToday = this.metricValue(this.matrixSummary, ["dailyShipmentCount", "DailyShipmentCount"]);
      return [
        { text: `${this.formatNumber(pastDue?.value || 0)} past due orders require action`, color: "#dc2626" },
        { text: `${this.formatNumber(mustShip?.value || 0)} orders are in Must Ship`, color: "#f59e0b" },
        { text: `${this.formatNumber(shippedToday)} orders shipped today`, color: DASHBOARD_GOOD_GREEN },
      ];
    },
    orderStatusBreakdown() {
      return this.normalizeBreakdown(
        this.firstArray(this.orderMatrixData, ["orderStatusBreakdown", "OrderStatusBreakdown", "statusBreakdown", "StatusBreakdown", "openOrderStatus"], ["status", "Status", "count", "Count"]),
        ["orderStatus", "OrderStatus", "status", "Status", "label", "Label"],
        ["orderCount", "OrderCount", "count", "Count", "orders", "Orders", "value", "Value"],
        ["percentageOfTotal", "PercentageOfTotal"],
      );
    },
    slaSummary() {
      return this.normalizeBreakdown(
        this.firstArray(this.orderMatrixData, ["slaCategories", "SlaCategories", "SLACategories", "orderSlaSummary", "OrderSlaSummary", "slaSummary", "SlaSummary", "SLASummary"], ["sla", "SLA", "slaCount", "SlaCount", "count", "Count"]),
        ["sla", "SLA", "status", "Status", "label", "Label"],
        ["slaCount", "SlaCount", "SLACount", "count", "Count", "orders", "Orders", "value", "Value"],
        ["slaPercent", "SlaPercent", "SLAPercent"],
        ["btocount", "btoCount", "BtoCount", "BTOCount"],
      ).map((item) => ({
        ...item,
        color: this.slaColor(item.label),
      }));
    },
    slaCategoryMetricCards() {
      return this.slaSummary.map((item) => ({
        label: item.label,
        value: item.value,
        icon: this.slaIcon(item.label),
        color: item.color,
        btoCount: this.isBtoSlaMetric(item.label) ? item.btoCount : null,
        loadingSource: "sla",
      }));
    },
    trendRows() {
      return this.firstArray(this.orderMatrixData, ["orderDroppedTrend", "OrderDroppedTrend", "ordersDroppedTrend", "OrdersDroppedTrend", "trend", "Trend", "dailyTrend", "DailyTrend"], ["date", "Date", "orderDate"])
        .map((item) => ({
          date: this.firstDefined([item.orderDate, item.OrderDate, item.date, item.Date, item.snapshotDate, item.SnapshotDate]) || "",
          ordersDropped: this.normalizeNumber(this.firstDefined([item.ordersCreated, item.OrdersCreated, item.ordersDropped, item.OrdersDropped, item.droppedOrders, item.DroppedOrders, item.count, item.Count])),
          dailyAverage: this.normalizeNumber(this.firstDefined([item.rollingAverage, item.RollingAverage, item.dailyAverage, item.DailyAverage, item.average, item.Average])),
          ordersShipped: this.normalizeNumber(this.firstDefined([item.ordersShipped, item.OrdersShipped, item.shippedOrders, item.ShippedOrders])),
          rollingShippingAverage: this.normalizeNumber(this.firstDefined([item.rollingShippingAverage, item.RollingShippingAverage, item.shippingAverage, item.ShippingAverage])),
        }));
    },
    statusTotal() {
      return this.orderStatusBreakdown.reduce((total, item) => total + item.value, 0);
    },
    statusBreakdownMax() {
      return Math.max(...this.orderStatusBreakdown.map((item) => item.value), 1);
    },
    slaTotal() {
      return this.slaSummary.reduce((total, item) => total + item.value, 0);
    },
    trendPoints() {
      return this.buildTrendPoints(this.trendRows, "ordersDropped");
    },
    trendPolyline() {
      return this.trendPoints.map((point) => `${point.x},${point.y}`).join(" ");
    },
    trendBars() {
      if (!this.trendRows.length) return [];
      const max = this.trendScaleMax;
      const chartWidth = TREND_CHART.right - TREND_CHART.left;
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      const slotWidth = chartWidth / this.trendRows.length;
      const groupWidth = Math.min(68, Math.max(22, slotWidth * 0.62));
      const barGap = Math.min(6, Math.max(3, groupWidth * 0.08));
      const barWidth = (groupWidth - barGap) / 2;
      return this.trendRows.map((item, index) => {
        const centerX = TREND_CHART.left + slotWidth * index + slotWidth / 2;
        const height = (item.ordersDropped / max) * chartHeight;
        const shippedHeight = (item.ordersShipped / max) * chartHeight;
        return {
          ...item,
          index,
          x: centerX - groupWidth / 2,
          createdX: centerX - groupWidth / 2,
          createdCenterX: centerX - groupWidth / 2 + barWidth / 2,
          shippedX: centerX - groupWidth / 2 + barWidth + barGap,
          shippedCenterX: centerX - groupWidth / 2 + barWidth + barGap + barWidth / 2,
          centerX,
          y: TREND_CHART.bottom - height,
          shippedY: TREND_CHART.bottom - shippedHeight,
          width: barWidth,
          groupWidth,
          height,
          shippedHeight,
        };
      });
    },
    trendAveragePoints() {
      return this.buildTrendPoints(this.trendRows, "dailyAverage");
    },
    trendAveragePolyline() {
      return this.trendAveragePoints.map((point) => `${point.x},${point.y}`).join(" ");
    },
    trendShippingAveragePoints() {
      return this.buildTrendPoints(this.trendRows, "rollingShippingAverage");
    },
    trendShippingAveragePolyline() {
      return this.trendShippingAveragePoints.map((point) => `${point.x},${point.y}`).join(" ");
    },
    trendGridLines() {
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      return this.trendYAxisTicks.map((tick) => ({
        value: tick,
        y: TREND_CHART.bottom - (tick / this.trendScaleMax) * chartHeight,
      }));
    },
    trendYAxisTicks() {
      const max = this.trendScaleMax;
      const step = max / 5;
      return Array.from({ length: 6 }, (_, index) => Math.round(step * index));
    },
    trendScaleMax() {
      const values = this.trendRows.flatMap((item) => [item.ordersDropped, item.dailyAverage, item.ordersShipped, item.rollingShippingAverage]);
      const max = Math.max(...values, 1);
      return Math.ceil(max / 5) * 5;
    },
    changeDirection() {
      return String(this.firstDefined([this.matrixSummary.changeDirection, this.matrixSummary.ChangeDirection]) || "NO CHANGE").toUpperCase();
    },
    orderDroppedPercentChange() {
      return this.metricValue(this.matrixSummary, ["percentChange", "PercentChange"]);
    },
    totalOrderChangeDirection() {
      return String(this.firstDefined([
        this.matrixSummary.totalOrderChangeDirection,
        this.matrixSummary.TotalOrderChangeDirection,
      ]) || "NO CHANGE").toUpperCase();
    },
    totalOrderPercentChange() {
      return this.metricValue(this.matrixSummary, ["percentTotalOrderChange", "PercentTotalOrderChange"]);
    },
    totalOrderTrendLabel() {
      const directionLabel = this.totalOrderChangeDirection === "NEW" || this.totalOrderChangeDirection === "UP"
        ? "Up"
        : this.totalOrderChangeDirection === "NO CHANGE"
          ? "No Change"
          : this.toProperCase(this.totalOrderChangeDirection);
      return `${directionLabel} ${this.formatTotalOrderChangePercent(this.totalOrderPercentChange)}`;
    },
    dashboardDrillTableHeight() {
      return Math.max(220, Math.floor(this.dashboardDrillWindowHeight * 0.9) - 245);
    },
    ordersDroppedTrendLoading() {
      return this.dashboardLoading.matrix || this.dashboardLoading.trend;
    },
    btoDrillColumnFilterDefinitions() {
      return this.btoDrillHeaders
        .filter((header) => this.isDrillColumnFilterHeader(header))
        .map((header) => ({
          label: this.drillColumnFilterLabel(header),
          value: header.value,
          options: this.drillColumnFilterOptions(header),
        }))
        .filter((filter) => filter.options.length > 1);
    },
    btoDrillFilteredRows() {
      return this.btoDrillRows.filter((row) => {
        const matchesColumnFilters = this.btoDrillColumnFilterDefinitions.every((filter) => {
          const selectedValues = this.btoDrillColumnFilters[filter.value] || [];
          if (!selectedValues.length) return true;
          return selectedValues.includes(this.drillColumnFilterValue(filter.value, row?.[filter.value]));
        });
        return matchesColumnFilters && this.drillRowMatchesSearch(row, this.btoDrillSearch);
      });
    },
    changeDirectionIcon() {
      if (this.changeDirection === "NEW" || this.changeDirection === "UP") return "mdi-trending-up";
      if (this.changeDirection === "DOWN") return "mdi-trending-down";
      return "mdi-trending-neutral";
    },
    changeDirectionLabel() {
      if (this.changeDirection === "NEW") return "New";
      if (this.changeDirection === "UP") return "Up";
      if (this.changeDirection === "DOWN") return "Down";
      return "No Change";
    },
  },
  async created() {
    this.route = this.$route.path.replace("/", "");
    await this.loadDashboardLayout();
    await this.loadBreadcrumbData();
    this.filters.asOfDate = currentAsOfDate();
    this.normalizeFilters();
    await this.loadFilterLists();
    await this.loadFilters();
  },
  mounted() {
    this.updateDashboardDrillWindowHeight();
    window.addEventListener("resize", this.updateDashboardDrillWindowHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDashboardDrillWindowHeight);
  },
  methods: {
    defaultDashboardLayout() {
      return DEFAULT_DASHBOARD_LAYOUT.map((section) => ({ ...section }));
    },
    async loadDashboardLayout() {
      const obj = { userid: this.userId, pass: 1, json: "" };
      const savedLayoutData = await salesService.postSalesOrderDashboardLayout("post", obj, false);
      const savedLayout = this.parseSavedDashboardLayout(savedLayoutData);
      this.dashboardLayout = savedLayout || this.defaultDashboardLayout();
      this.dashboardLayoutDraft = this.cloneDashboardLayout(this.dashboardLayout);
    },
    parseSavedDashboardLayout(value) {
      if (!value) return null;
      try {
        let source = Array.isArray(value) && value.length === 1 && !value[0]?.id ? value[0] : value;
        const result = this.firstDefined([source.Result, source.result]);
        if (Array.isArray(result) && result.length) {
          source = result.some((item) => item?.id) ? result : result[0];
        }
        const jsonValue = this.firstDefined([
          source.json,
          source.Json,
          source.JSON,
          source.layout_json,
          source.LayoutJson,
          source.dashboard_layout,
          source.DashboardLayout,
        ]);
        if (typeof jsonValue === "string" && jsonValue.trim()) {
          source = JSON.parse(jsonValue);
        }
        const parsedLayout = typeof source === "string" ? JSON.parse(source) : source;
        if (!Array.isArray(parsedLayout)) return null;
        return this.mergeSavedDashboardLayout(parsedLayout);
      } catch (err) {
        return null;
      }
    },
    mergeSavedDashboardLayout(savedLayout) {
      const defaultSections = this.defaultDashboardLayout();
      const defaultById = defaultSections.reduce((sections, section) => ({
        ...sections,
        [section.id]: section,
      }), {});
      const knownSavedSections = savedLayout
        .filter((section) => defaultById[section?.id])
        .map((section) => ({
          ...defaultById[section.id],
          visible: section.visible !== false,
        }));
      const missingSections = defaultSections.filter(
        (section) => !knownSavedSections.some((savedSection) => savedSection.id === section.id)
      );
      return [...knownSavedSections, ...missingSections];
    },
    cloneDashboardLayout(layout) {
      return layout.map((section) => ({ ...section }));
    },
    startDashboardCustomize() {
      this.dashboardLayoutDraft = this.cloneDashboardLayout(this.dashboardLayout);
      this.isDashboardCustomizing = true;
    },
    cancelDashboardCustomize() {
      this.dashboardLayoutDraft = this.cloneDashboardLayout(this.dashboardLayout);
      this.isDashboardCustomizing = false;
    },
    async saveDashboardCustomize() {
      this.dashboardLayout = this.cloneDashboardLayout(this.dashboardLayoutDraft);
      await this.saveDashboardLayout();
      this.isDashboardCustomizing = false;
    },
    resetDashboardCustomize() {
      this.dashboardLayoutDraft = this.defaultDashboardLayout();
    },
    async saveDashboardLayout() {
      await this.saveDashboardLayoutSettings(this.dashboardLayout);
    },
    async saveDashboardLayoutSettings(layout) {
      const layoutPayload = layout.map((section) => ({
        id: section.id,
        visible: section.visible !== false,
      }));
      const obj = { userid: this.userId, pass: 2, json: JSON.stringify(layoutPayload) };
      await salesService.postSalesOrderDashboardLayout("post", obj, false);
    },
    toggleDashboardSection(sectionId, visible) {
      const section = this.dashboardLayoutDraft.find((item) => item.id === sectionId);
      if (section) section.visible = visible;
    },
    isDashboardSectionVisible(sectionId) {
      const layoutSource = this.isDashboardCustomizing ? this.dashboardLayoutDraft : this.dashboardLayout;
      const section = layoutSource.find((item) => item.id === sectionId);
      return section ? section.visible !== false : true;
    },
    dashboardSectionOrder(sectionId) {
      const layoutSource = this.isDashboardCustomizing ? this.dashboardLayoutDraft : this.dashboardLayout;
      const sectionIndex = layoutSource.findIndex((item) => item.id === sectionId);
      return sectionIndex >= 0 ? sectionIndex : 0;
    },
    dashboardSectionHiddenClass(sectionId) {
      return {
        "dashboard-customize-section-hidden": this.isDashboardCustomizing && !this.isDashboardSectionVisible(sectionId),
      };
    },
    toggleTrendExpanded() {
      this.isTrendExpanded = !this.isTrendExpanded;
    },
    updateDashboardDrillWindowHeight() {
      this.dashboardDrillWindowHeight = window.innerHeight;
    },
    async loadBreadcrumbData() {
      const data = await AddFavourite.getPageAction(this.userId, this.route);
      this.actionList = data?.actionList || [];
      this.mainPage = data?.mainPage || "Sales Order";
      this.favouriteList = data?.favouriteList || [];
      this.pageUrl = data?.pageUrl || this.route;
      this.subPage = data?.subPage || "Sales Order Dashboard";
      this.showAdd = data?.showAdd || false;
    },
    addFav() {
      this.showAdd = !this.showAdd;
      AddFavourite.add_remove_Favourite(this.showAdd, this.userId, this.route);
    },
    resetFunction() {
      this.refreshDashboard();
    },
    normalizeFilters() {
      if (!Array.isArray(this.filters.project)) this.filters.project = this.filters.project ? [this.filters.project] : [];
      if (!Array.isArray(this.filters.business)) this.filters.business = this.filters.business ? [this.filters.business] : [];
      if (!Array.isArray(this.filters.facility)) this.filters.facility = this.filters.facility ? [this.filters.facility] : [];
      this.filters.project = this.normalizeSelectedIds(this.filters.project);
      this.filters.business = this.normalizeSelectedIds(this.filters.business);
      this.filters.facility = this.normalizeSelectedIds(this.filters.facility);
      if (Array.isArray(this.filters.trendDays)) this.filters.trendDays = this.filters.trendDays[0] || DEFAULT_FILTERS.trendDays;
    },
    async loadFilters() {
      const obj = { userid: this.userId, pass: 1, json: "" };
      const savedFilters = await salesService.postSalesOrderDashboardFilter("post", obj, false);
      const parsedFilters = this.parseSavedDashboardFilters(savedFilters);
      if (!parsedFilters) return;

      this.filters = {
        ...DEFAULT_FILTERS,
        ...parsedFilters,
        asOfDate: currentAsOfDate(),
      };
      this.normalizeFilters();
      await this.loadFilterLists();
      await this.loadOrderMatrix();
    },
    async saveFilters() {
      this.normalizeFilters();
      const jsonPayload = JSON.stringify({
        project: this.filters.project,
        business: this.filters.business,
        facility: this.filters.facility,
        trendDays: this.trendDaysPayload,
      });
      const obj = { userid: this.userId, pass: 2, json: jsonPayload };
      await salesService.postSalesOrderDashboardFilter("post", obj, false);
    },
    async clearSavedFilters() {
      const obj = { userid: this.userId, pass: 2, json: "" };
      await salesService.postSalesOrderDashboardFilter("post", obj, false);
    },
    async refreshDashboard() {
      this.normalizeFilters();
      await this.loadFilterLists();
      await this.loadOrderMatrix();
    },
    async clearFilters() {
      this.filters = { ...DEFAULT_FILTERS, asOfDate: currentAsOfDate() };
      this.orderMatrixData = {};
      this.btoDashboardData = {};
      this.orderMatrixLoaded = false;
      this.dashboardMetricRequestId += 1;
      this.resetDashboardLoading();
      this.dashboardInsight = null;
      this.hasPendingDashboardFilterChanges = false;
      await this.clearSavedFilters();
      await this.loadFilterLists();
    },
    async onDashboardFilterSelectionChange(filterRef = "") {
      if (this.suppressNextDashboardFilterChange) return;
      this.normalizeFilters();
      this.hasPendingDashboardFilterChanges = true;
      await this.$nextTick();
      if (!this.isDashboardFilterDropdownOpen(filterRef)) {
        await this.applyDashboardFilters(filterRef);
      }
    },
    getDashboardFilterSelectionText(item) {
      return item?.text ?? item;
    },
    getDashboardFilterSelectionValue(item) {
      return item?.value ?? item;
    },
    async removeDashboardFilterSelection(filterName, item, filterRef = "") {
      const removedValue = this.getDashboardFilterSelectionValue(item);
      this.filters[filterName] = (this.filters[filterName] || []).filter(
        (value) => String(value) !== String(removedValue)
      );
      this.hasPendingDashboardFilterChanges = true;
      await this.applyDashboardFilters(filterRef);
    },
    async clearDashboardFilterSelection(filterName, filterRef = "") {
      this.suppressNextDashboardFilterChange = true;
      this.filters[filterName] = [];
      this.hasPendingDashboardFilterChanges = true;
      try {
        await this.$nextTick();
        await this.applyDashboardFilters(filterRef);
        await this.$nextTick();
      } finally {
        this.suppressNextDashboardFilterChange = false;
      }
    },
    applyPendingDashboardFilters(filterRef = "") {
      if (!this.hasPendingDashboardFilterChanges || this.isApplyingDashboardFilters) return;
      if (this.dashboardPendingApplyTimer) {
        clearTimeout(this.dashboardPendingApplyTimer);
      }
      this.dashboardPendingApplyTimer = setTimeout(async () => {
        this.dashboardPendingApplyTimer = null;
        if (this.hasPendingDashboardFilterChanges && !this.isApplyingDashboardFilters) {
          await this.applyDashboardFilters(filterRef);
        }
      }, 150);
    },
    async applyDashboardFilters(filterRef = "") {
      if (this.isApplyingDashboardFilters) return;
      if (this.dashboardPendingApplyTimer) {
        clearTimeout(this.dashboardPendingApplyTimer);
        this.dashboardPendingApplyTimer = null;
      }
      this.isApplyingDashboardFilters = true;
      try {
        this.normalizeFilters();
        await this.saveFilters();
        await this.loadFilterLists();
        await this.loadOrderMatrix();
        this.hasPendingDashboardFilterChanges = false;
        this.closeDashboardFilterDropdown(filterRef);
      } finally {
        this.isApplyingDashboardFilters = false;
      }
    },
    closeDashboardFilterDropdown(filterRef) {
      if (!filterRef || !this.$refs?.[filterRef]) return;
      this.$nextTick(() => {
        const filter = this.$refs[filterRef];
        if (filter?.blur) filter.blur();
        if (filter?.isMenuActive !== undefined) filter.isMenuActive = false;
      });
    },
    isDashboardFilterDropdownOpen(filterRef) {
      if (!filterRef || !this.$refs?.[filterRef]) return false;
      return Boolean(this.$refs[filterRef]?.isMenuActive);
    },
    async onTrendDaysChange() {
      this.normalizeFilters();
      await this.saveFilters();
      await this.loadOrdersDroppedTrend();
    },
    async onAsOfDateChange() {
      this.asOfDateMenu = false;
      this.normalizeFilters();
      await this.loadFilterLists();
      await this.loadOrderMatrix();
    },
    parseSavedDashboardFilters(savedFilters) {
      if (!savedFilters || savedFilters?.message === "NA") return null;
      let source = savedFilters;
      const jsonValue = this.firstDefined([
        savedFilters.json,
        savedFilters.Json,
        savedFilters.JSON,
        savedFilters.filter_json,
        savedFilters.FilterJson,
        savedFilters.dashboard_filters,
        savedFilters.DashboardFilters,
      ]);
      if (typeof jsonValue === "string" && jsonValue.trim()) {
        try {
          source = JSON.parse(jsonValue);
        } catch (err) {
          source = savedFilters;
        }
      }
      if (typeof source === "string") {
        try {
          source = JSON.parse(source);
        } catch (err) {
          return null;
        }
      }

      const project = this.firstDefined([
        source.project,
        source.projects,
        source.project_list,
        source.proj_key,
        source.proj_key_list,
        source.Proj_key,
        source.ProjKey,
      ]);
      const business = this.firstDefined([
        source.business,
        source.business_list,
        source.bu_key,
        source.bu_key_list,
        source.Bu_key,
        source.BuKey,
      ]);
      const facility = this.firstDefined([
        source.facility,
        source.facility_list,
        source.facility_id,
        source.facility_id_list,
        source.FacilityID,
        source.FacilityId,
      ]);
      const trendDays = this.firstDefined([
        source.trendDays,
        source.trend_days,
        source.TrendDays,
      ]);

      const filters = {
        project: this.toSavedFilterArray(project),
        business: this.toSavedFilterArray(business),
        facility: this.toSavedFilterArray(facility),
        trendDays: this.normalizeTrendDays(trendDays),
      };
      const hasSavedSelection = filters.project.length ||
        filters.business.length ||
        filters.facility.length ||
        trendDays !== undefined;
      return hasSavedSelection ? filters : null;
    },
    toSavedFilterArray(value) {
      if (value === undefined || value === null || value === "") return [];
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsedValue = JSON.parse(value);
          return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
        } catch (err) {
          return value.split(",").map((item) => item.trim()).filter(Boolean);
        }
      }
      return [value];
    },
    normalizeTrendDays(value) {
      const trendDays = Array.isArray(value) ? value[0] : value;
      const numericTrendDays = parseInt(trendDays);
      return Number.isInteger(numericTrendDays) ? numericTrendDays : DEFAULT_FILTERS.trendDays;
    },
    async loadFilterLists() {
      const filterData = await salesService.getSalesOrderDashboardFilterList(
        "get",
        parseInt(this.userId),
        this.projectKeyPayload,
        this.businessKeyPayload,
        this.facilityIdPayload,
      );
      if (filterData?.message === "NA") return;

      this.projectList = this.toSelectItems(this.firstArray(filterData, ["projects", "Projects", "projectList", "ProjectList", "ProjectInfo", "Project", "project_list"], ["projectID", "projectId", "ProjKey", "proj_key", "proj_id", "ProjectId", "project_id"]));
      this.businessList = this.toSelectItems(
        this.firstArray(filterData, ["businessUnits", "BusinessUnits", "BusinessInfo", "Businesses", "BusinessList", "ProjectSpecificBusinessInfo", "BUInfo", "BuInfo", "Business", "business_list", "businesses", "bu_list"], ["businessUnitID", "businessUnit", "Bu_id", "BuKey", "BuId", "bu_id", "bu_key", "business_id", "BusinessName", "business"]),
      );
      this.facilityList = this.toSelectItems(this.firstArray(filterData, ["facilities", "Facilities", "facilityList", "FacilityList", "FacilityInfo", "Facility", "facility_list"], ["facilityID", "facilityId", "FacilityID", "FacilityKey", "FacilityId", "facilityid", "facility_key", "facility_id", "LocaKey", "loca_key"]));
    },
    async loadOrderMatrix() {
      const requestId = this.dashboardMetricRequestId + 1;
      this.dashboardMetricRequestId = requestId;
      this.orderMatrixLoaded = true;
      this.orderMatrixData = {};
      this.btoDashboardData = {};
      this.shipAuditDashboardData = {};
      this.setAllDashboardLoading(true);
      this.dashboardInsight = null;

      const userId = parseInt(this.userId);
      const trendDays = this.trendDaysPayload;
      const asOfDate = this.asOfDatePayload;
      const facilityId = this.facilityIdPayload;
      const projectKey = this.projectKeyPayload;
      const businessKey = this.businessKeyPayload;
      const disableLoader = true;

      const metricCalls = [
        this.loadDashboardMetricChunk("orderStatus", requestId, salesService.getSalesOrderDashboardOrderStatus(
          "get",
          userId,
          trendDays,
          asOfDate,
          facilityId,
          projectKey,
          businessKey,
          disableLoader,
        )),
        this.loadDashboardMetricChunk("sla", requestId, salesService.getSalesOrderDashboardSla(
          "get",
          userId,
          trendDays,
          asOfDate,
          facilityId,
          projectKey,
          businessKey,
          disableLoader,
        )),
        this.loadDashboardMetricChunk("bto", requestId, salesService.getSalesOrderDashboardBto(
          "get",
          userId,
          asOfDate,
          facilityId,
          projectKey,
          businessKey,
          disableLoader,
        )),
        this.loadDashboardMetricChunk("matrix", requestId, salesService.getSalesOrderDashboardOrderMatrix(
          "get",
          userId,
          trendDays,
          asOfDate,
          facilityId,
          projectKey,
          businessKey,
          disableLoader,
        )),
        this.loadDashboardMetricChunk("shipAudit", requestId, salesService.getSalesOrderDashboardShipAudit(
          "get",
          userId,
          asOfDate,
          facilityId,
          projectKey,
          businessKey,
          disableLoader,
        )),
      ];
      await Promise.allSettled(metricCalls);
    },
    async loadOrdersDroppedTrend() {
      const requestId = this.dashboardMetricRequestId + 1;
      this.dashboardMetricRequestId = requestId;
      this.setDashboardLoading("trend", true);
      this.dashboardInsight = null;

      try {
        const data = await salesService.getSalesOrderDashboardOrderMatrix(
          "get",
          parseInt(this.userId),
          this.trendDaysPayload,
          this.asOfDatePayload,
          this.facilityIdPayload,
          this.projectKeyPayload,
          this.businessKeyPayload,
          true,
        );
        if (requestId === this.dashboardMetricRequestId) {
          this.applyDashboardTrendData(data);
        }
      } finally {
        if (requestId === this.dashboardMetricRequestId) {
          this.setDashboardLoading("trend", false);
        }
      }
    },
    async loadDashboardMetricChunk(source, requestId, metricPromise) {
      try {
        const data = await metricPromise;
        this.applyDashboardMetricData(data, requestId, source);
      } finally {
        if (requestId === this.dashboardMetricRequestId) {
          this.setDashboardLoading(source, false);
        }
      }
    },
    async openBtoDrillDialog() {
      await this.openDashboardDrillDialog("BTO", "", "BTO Details");
    },
    async openBtoTileDrillDialog(metric) {
      const parameter = metric?.drillParameter || "";
      await this.openDashboardDrillDialog("BTO", parameter, `${metric?.label || "BTO"} Details`);
    },
    async openActiveBtoStatusDrill(item) {
      const btoStatus = item?.label || "";
      await this.openDashboardDrillDialog("ACTIVEBTO", btoStatus, `Active BTO Status - ${btoStatus}`);
    },
    async openKpiDrillDialog(metric) {
      const parameter = metric?.label || "";
      await this.openDashboardDrillDialog("KPI", parameter, `${parameter} Order Details`);
    },
    async openOrderVolumeDrillDialog(metric) {
      const drillType = this.orderVolumeDrillType(metric);
      if (!drillType) return;
      await this.openDashboardDrillDialog(drillType, "", `${metric.label} Details`);
    },
    async openOrderStatusDrillDialog(item) {
      const orderStatus = item?.label || "";
      await this.openDashboardDrillDialog("ORDSTATUS", orderStatus, `Order Status - ${orderStatus}`);
    },
    isShipmentAuditCardInteractive(metric) {
      return Boolean(metric?.drillType);
    },
    async openShipmentAuditCardDrill(metric) {
      if (!this.isShipmentAuditCardInteractive(metric)) return;
      await this.openDashboardDrillDialog(metric.drillType, metric.drillParameter || metric.label, `${metric.label} Details`);
    },
    async openShipmentAuditStatusDrill(item) {
      const auditStatus = item?.label || "";
      await this.openDashboardDrillDialog("SHIPAUDITSTATUS", auditStatus, `Audits In Progress - ${auditStatus}`);
    },
    orderVolumeDrillType(metric) {
      const drillType = String(metric?.drillType || "").toUpperCase();
      if (["DROPPED", "SHIPPED", "CANCELED"].includes(drillType)) return drillType;
      return "";
    },
    async openDashboardDrillDialog(drillType, parameter, title) {
      this.btoDrillDialog = true;
      this.btoDrillTitle = title;
      this.dashboardDrillType = String(drillType || "").toUpperCase();
      this.btoDrillNoDataText = this.dashboardDrillNoDataText(drillType);
      this.btoDrillLoading = true;
      this.btoDrillRows = [];
      this.btoDrillHeaders = [];
      this.btoDrillSearch = "";
      this.btoDrillColumnFilters = {};
      try {
        const drillData = await salesService.getSalesOrderDashboardDrill(
          "get",
          parseInt(this.userId),
          this.asOfDatePayload,
          this.facilityIdPayload,
          this.projectKeyPayload,
          this.businessKeyPayload,
          drillType,
          parameter,
        );
        this.btoDrillRows = this.parseDashboardDrillRows(drillData);
        this.btoDrillHeaders = this.buildDrillHeaders(this.btoDrillRows);
      } finally {
        this.btoDrillLoading = false;
      }
    },
    parseDashboardDrillRows(drillData) {
      const normalizedData = this.normalizeDashboardMetricPayload(drillData);
      const messagePayload = this.firstDefined([
        normalizedData?.Message,
        normalizedData?.message,
        normalizedData?.MESSAGE,
      ]);
      const parsedPayload = this.parseJsonValue(messagePayload !== undefined ? messagePayload : normalizedData);
      const rows = Array.isArray(parsedPayload) ? parsedPayload : this.firstNestedArray(parsedPayload);
      return rows.map((row) => (row && typeof row === "object" && !Array.isArray(row) ? row : { Value: row }));
    },
    parseJsonValue(value) {
      if (typeof value !== "string") return value;
      if (!value.trim()) return [];
      try {
        return JSON.parse(value);
      } catch (err) {
        return value;
      }
    },
    firstNestedArray(value) {
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== "object") return [];
      for (const item of Object.values(value)) {
        const parsedItem = this.parseJsonValue(item);
        const nestedArray = this.firstNestedArray(parsedItem);
        if (nestedArray.length) return nestedArray;
      }
      return [];
    },
    buildDrillHeaders(rows) {
      const keys = rows.reduce((headers, row) => {
        Object.keys(row || {}).forEach((key) => {
          if (!headers.includes(key)) headers.push(key);
        });
        return headers;
      }, []);
      return keys.filter((key) => !this.isHiddenDrillIdColumn(key)).map((key) => ({
        text: key,
        value: key,
        sortable: true,
        class: "primary customwhite--text",
      }));
    },
    async exportBtoDrillExcel() {
      if (!this.btoDrillFilteredRows.length) return;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("BTO Drill");
      const keys = this.btoDrillHeaders.map((header) => header.value);
      worksheet.addRow(keys);
      this.btoDrillFilteredRows.forEach((row) => {
        worksheet.addRow(keys.map((key) => this.formatDrillValue(row[key])));
      });
      worksheet.getRow(1).font = { bold: true };
      worksheet.columns.forEach((column) => {
        column.width = 18;
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), Utility.excelNewFileName(this.dashboardDrillFileName()));
    },
    exportBtoDrillPdf() {
      if (!this.btoDrillRows.length || !this.$refs.btoDrillPdf) return;
      html2pdf(this.$refs.btoDrillPdf, {
        margin: 0.25,
        filename: Utility.excelFileName(this.dashboardDrillFileName()).replace(".xls", ".pdf"),
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "letter", orientation: "landscape" },
      });
    },
    dashboardDrillFileName() {
      return String(this.btoDrillTitle || "Sales_Order_Dashboard_Drill")
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_+|_+$/g, "") || "Sales_Order_Dashboard_Drill";
    },
    dashboardDrillNoDataText(drillType) {
      const normalizedDrillType = String(drillType || "").toUpperCase();
      if (["SHIPAUDIT", "SHIPAUDITSTATUS"].includes(normalizedDrillType)) return "No Shipment Audit data available.";
      return ["KPI", "DROPPED", "SHIPPED", "CANCELED", "ORDSTATUS"].includes(normalizedDrillType)
        ? "No order detail data available."
        : "No BTO detail data available.";
    },
    formatDrillValue(value) {
      if (value === undefined || value === null) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    },
    drillRowMatchesSearch(row, search) {
      const searchText = String(search || "").trim().toLowerCase();
      if (!searchText) return true;
      return this.btoDrillHeaders.some((header) =>
        String(this.formatDrillValue(row?.[header.value])).toLowerCase().includes(searchText)
      );
    },
    drillTableFilter(value, search) {
      if (!search) return true;
      return String(this.formatDrillValue(value)).toLowerCase().includes(String(search).toLowerCase());
    },
    drillTableSort(items, sortBy, sortDesc) {
      if (!sortBy?.length) return items;
      return [...items].sort((left, right) => {
        for (let index = 0; index < sortBy.length; index += 1) {
          const key = sortBy[index];
          const direction = sortDesc[index] ? -1 : 1;
          const result = this.compareDrillValues(left?.[key], right?.[key]);
          if (result !== 0) return result * direction;
        }
        return 0;
      });
    },
    compareDrillValues(left, right) {
      const leftValue = this.normalizeDrillSortValue(left);
      const rightValue = this.normalizeDrillSortValue(right);
      if (leftValue === rightValue) return 0;
      if (leftValue === "") return 1;
      if (rightValue === "") return -1;
      if (typeof leftValue === "number" && typeof rightValue === "number") return leftValue - rightValue;
      return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: "base" });
    },
    normalizeDrillSortValue(value) {
      const formattedValue = String(this.formatDrillValue(value)).trim();
      if (!formattedValue) return "";
      const numericValue = Number(formattedValue.replace(/[$,%\s,]/g, ""));
      if (Number.isFinite(numericValue) && /^-?[$\s]*[\d,]+(\.\d+)?%?$/.test(formattedValue)) return numericValue;
      const dateValue = moment(formattedValue, ["MM/DD/YYYY", "YYYY-MM-DD", "MM/DD/YYYY HH:mm:ss", moment.ISO_8601], true);
      if (dateValue.isValid()) return dateValue.valueOf();
      return formattedValue.toUpperCase();
    },
    isDrillColumnFilterHeader(header) {
      return Boolean(this.drillColumnFilterType(header));
    },
    drillColumnFilterLabel(header) {
      const filterType = this.drillColumnFilterType(header);
      if (filterType === "STOPLIGHT") return "Stoplight";
      if (filterType === "ORDER_TYPE") return "Order Type";
      return "Order Status";
    },
    drillColumnFilterType(header) {
      const columnName = this.normalizeDrillColumnName(header?.value);
      const compactColumnName = columnName.replace(/\s/g, "");
      if (compactColumnName.includes("STOPLIGHT") || compactColumnName === "SLASTATUS") return "STOPLIGHT";
      if (
        compactColumnName === "ORDERTYPE" ||
        compactColumnName === "SOTYPE" ||
        compactColumnName === "SALESORDERTYPE" ||
        compactColumnName === "TYPE" ||
        (columnName.includes("ORDER") && columnName.includes("TYPE"))
      ) {
        return "ORDER_TYPE";
      }
      if (
        compactColumnName === "STATUS" ||
        compactColumnName === "ORDERSTATUS" ||
        compactColumnName === "SOSTATUS" ||
        compactColumnName === "BTOSTATUS" ||
        compactColumnName === "SALESORDERSTATUS" ||
        compactColumnName === "ORDERSTATUSCODE" ||
        (columnName.includes("ORDER") && columnName.includes("STATUS"))
      ) {
        return "ORDER_STATUS";
      }
      return "";
    },
    drillColumnFilterOptions(header) {
      const optionMap = new Map();
      this.btoDrillRows.forEach((row) => {
        const value = this.drillColumnFilterValue(header.value, row?.[header.value]);
        if (!optionMap.has(value)) {
          optionMap.set(value, {
            text: value || "(Blank)",
            value,
          });
        }
      });
      return [...optionMap.values()].sort((left, right) =>
        String(left.text).localeCompare(String(right.text), undefined, { numeric: true, sensitivity: "base" })
      );
    },
    drillColumnFilterValue(column, value) {
      if (this.normalizeDrillColumnName(column).replace(/\s/g, "") === "STOPLIGHT") {
        return this.normalizeStopLight(value) || "";
      }
      return String(this.formatDrillValue(value)).trim();
    },
    setBtoDrillColumnFilter(column, values) {
      this.$set(this.btoDrillColumnFilters, column, values || []);
    },
    isStopLightColumn(header) {
      return String(header?.value || "").toUpperCase() === "STOPLIGHT";
    },
    isHiddenDrillIdColumn(key) {
      return ["so_id", "so_key", "order_id", "sales_order_id", "kit_id", "ship_audit_id", "audit_id"]
        .map((columnName) => this.normalizeDrillColumnName(columnName).replace(/\s/g, ""))
        .includes(this.normalizeDrillColumnName(key).replace(/\s/g, ""));
    },
    isSalesOrderNumberColumn(header) {
      const columnName = this.normalizeDrillColumnName(header?.value);
      return ["SO", "SO #", "SO NO", "SO NUMBER", "SONO", "SONUMBER", "ORDER NUMBER", "SALES ORDER", "SALES ORDER NUMBER", "SALESORDER", "SALESORDERNUMBER"].includes(columnName) ||
        (columnName.includes("SO") && columnName.includes("NUMBER")) ||
        (columnName.includes("SALES") && columnName.includes("ORDER"));
    },
    isBtoNumberColumn(header) {
      return ["BTO", "BTO NUMBER", "BTO KIT", "BTO KIT NUMBER", "BTO/KIT", "BTO/KIT NUMBER", "ACTIVE BTO", "ACTIVEBTO", "ACTIVE BTO NUMBER"].includes(
        this.normalizeDrillColumnName(header?.value)
      );
    },
    isShippingAuditNumberColumn(header) {
      const columnNames = [header?.value, header?.text]
        .map((value) => this.normalizeDrillColumnName(value))
        .filter(Boolean);
      return columnNames.some((columnName) => {
        const compactColumnName = columnName.replace(/\s/g, "");
        return ["AUDIT", "AUDIT #", "AUDIT NO", "AUDIT NUMBER", "AUDITNUMBER", "SHIP AUDIT", "SHIP AUDIT NUMBER", "SHIPMENT AUDIT", "SHIPMENT AUDIT NUMBER", "SHIP AUDIT #", "SHIPMENT AUDIT #", "SA", "SA #", "SA NUMBER", "SHIP AUDIT NO", "SHIPMENT AUDIT NO"].includes(columnName) ||
          ["AUDITNO", "AUDITNUMBER", "AUDITNUM", "AUDIT#", "SHIPAUDIT", "SHIPAUDITNO", "SHIPAUDITNUMBER", "SHIPAUDITNUM", "SHIPAUDIT#", "SHIPMENTAUDIT", "SHIPMENTAUDITNO", "SHIPMENTAUDITNUMBER", "SHIPMENTAUDITNUM", "SHIPMENTAUDIT#", "SANUMBER", "SANO"].includes(compactColumnName) ||
          (columnName.includes("AUDIT") && (columnName.includes("NUMBER") || columnName.includes("NUM") || columnName.includes("NO") || columnName.includes("#")));
      });
    },
    normalizeDrillColumnName(value) {
      return String(value || "")
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
    },
    canLinkSalesOrder(header, item) {
      return this.isSalesOrderNumberColumn(header) && this.salesOrderDrillId(item);
    },
    canLinkBto(header, item) {
      return this.isBtoNumberColumn(header) && this.btoDrillRouteValue(item, header) && !this.isCanceledBtoDrillRow(item);
    },
    canLinkShippingAudit(header, item) {
      if (this.isShippingAuditNumberColumn(header)) return true;
      const auditNumber = this.shippingAuditNumberValue(item);
      return auditNumber !== undefined && String(item?.[header?.value]) === String(auditNumber);
    },
    salesOrderDrillId(item) {
      return this.firstDefined([
        item?.so_id,
        item?.SO_ID,
        item?.So_Id,
        item?.soId,
        item?.SoId,
        item?.so_key,
        item?.SO_KEY,
        item?.So_Key,
        item?.order_id,
        item?.OrderId,
        item?.orderId,
        item?.sales_order_id,
        item?.SalesOrderId,
      ]);
    },
    btoDrillRouteValue(item, header) {
      return this.firstDefined([
        item?.[header?.value],
        item?.BTO,
        item?.bto,
        item?.bono,
        item?.Bono,
        item?.["BTO/KIT #"],
        item?.["BTO/Kit #"],
      ]);
    },
    shippingAuditDrillId(item) {
      return this.drillRowValueByColumnNames(item, [
        "ship_audit_id",
        "Ship_Audit_Id",
        "ShipAuditId",
        "shipAuditId",
        "SHIP_AUDIT_ID",
        "audit_id",
        "Audit_Id",
        "AuditId",
      ]);
    },
    shippingAuditRouteId(item, header) {
      return this.shippingAuditDrillId(item) ||
        this.shippingAuditIdFromAuditNumber(this.shippingAuditNumberValue(item)) ||
        this.shippingAuditIdFromAuditNumber(item?.[header?.value]);
    },
    shippingAuditIdFromAuditNumber(value) {
      const auditNumber = String(this.formatDrillValue(value) || "").trim();
      if (!auditNumber) return "";
      const numericAuditId = auditNumber.match(/\d+/g)?.join("");
      if (!numericAuditId) return "";
      return numericAuditId.replace(/^0+/, "") || "0";
    },
    shippingAuditNumberValue(item) {
      return this.drillRowValueByColumnNames(item, [
        "audit",
        "Audit",
        "audit_number",
        "Audit_Number",
        "AuditNumber",
        "auditNumber",
        "audit_no",
        "Audit_No",
        "AuditNo",
        "audit_num",
        "Audit_Num",
        "AuditNum",
        "ship_audit",
        "Ship_Audit",
        "ShipAudit",
        "ship_audit_number",
        "Ship_Audit_Number",
        "ShipAuditNumber",
        "shipAuditNumber",
        "ship_audit_no",
        "Ship_Audit_No",
        "ShipAuditNo",
        "shipment_audit",
        "Shipment_Audit",
        "ShipmentAudit",
        "shipment_audit_number",
        "Shipment_Audit_Number",
        "ShipmentAuditNumber",
        "sa_number",
        "SA_Number",
        "SANumber",
      ]);
    },
    drillRowValueByColumnNames(item, columnNames) {
      if (!item || typeof item !== "object") return undefined;
      const normalizedColumnNames = columnNames.map((columnName) => this.normalizeDrillColumnName(columnName).replace(/\s/g, ""));
      const rowKey = Object.keys(item).find((key) =>
        normalizedColumnNames.includes(this.normalizeDrillColumnName(key).replace(/\s/g, ""))
      );
      return rowKey ? item[rowKey] : undefined;
    },
    isCanceledBtoDrillRow(item) {
      const status = String(this.firstDefined([
        item?.status,
        item?.Status,
        item?.STATUS,
        item?.btoStatus,
        item?.BtoStatus,
        item?.BTOStatus,
        item?.["BTO Status"],
        item?.["BTO_Status"],
      ]) || "").toUpperCase();
      return status.includes("CANCEL");
    },
    openSalesOrderDrillLink(item) {
      const soId = this.salesOrderDrillId(item);
      if (!soId) return;
      const resolvedRoute = this.$router.resolve(`/sales-order-details/${btoa(soId.toString())}`);
      window.open(resolvedRoute.href, "_blank");
    },
    openBtoDrillLink(item, header) {
      const btoValue = this.btoDrillRouteValue(item, header);
      if (!btoValue) return;
      const resolvedRoute = this.$router.resolve(`/bto-details/${btoa(btoValue.toString())}`);
      window.open(resolvedRoute.href, "_blank");
    },
    openShippingAuditDrillLink(item, header) {
      const auditId = this.shippingAuditRouteId(item, header);
      if (!auditId) return;
      const auditIdValue = auditId.toString();
      EncryptUtility.localStorageEncrypt("ShippingAuditID", auditIdValue);
      const encodedAuditId = btoa(auditIdValue);
      const resolvedRoute = this.$router.resolve({
        name: "shippingAuditQueueDetails",
        params: {
          audit_id: encodedAuditId,
        },
        query: {
          audit_id: encodedAuditId,
        },
      });
      window.open(resolvedRoute.href, "_blank");
    },
    drillStopLightDisplay(value) {
      return this.normalizeStopLight(value) || "-";
    },
    drillStopLightColor(value) {
      const stopLight = this.normalizeStopLight(value);
      if (stopLight === "ON-TIME") return "#16a34a";
      if (stopLight === "MUST SHIP") return "#f59e0b";
      if (stopLight === "PAST DUE") return "#dc2626";
      if (stopLight === "SHIPPED") return this.$vuetify.theme.dark ? "#90CAF9" : "#1565C0";
      if (stopLight === "N/A") return "#ECEFF1";
      return "#64748b";
    },
    isLightStopLightChip(value) {
      return this.normalizeStopLight(value) === "N/A";
    },
    normalizeStopLight(value) {
      const stopLight = String(value || "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim()
        .toUpperCase();
      const compactStopLight = stopLight.replace(/[\s-_/\\.]/g, "");
      if (compactStopLight.includes("ONTIME")) return "ON-TIME";
      if (compactStopLight.includes("MUSTSHIP")) return "MUST SHIP";
      if (compactStopLight.includes("PASTDUE")) return "PAST DUE";
      if (compactStopLight.includes("SHIPPED")) return "SHIPPED";
      if (compactStopLight === "NA" || compactStopLight === "NOTAPPLICABLE") return "N/A";
      return stopLight;
    },
    setDashboardLoading(source, isLoading) {
      this.dashboardLoading = {
        ...this.dashboardLoading,
        [source]: isLoading,
      };
    },
    setAllDashboardLoading(isLoading) {
      this.dashboardLoading = {
        orderStatus: isLoading,
        sla: isLoading,
        bto: isLoading,
        matrix: isLoading,
        shipAudit: isLoading,
        trend: false,
      };
    },
    resetDashboardLoading() {
      this.setAllDashboardLoading(false);
    },
    isDashboardSectionLoading(source) {
      return Boolean(source && this.dashboardLoading?.[source]);
    },
    findSlaMetricCard(labelTerms) {
      return this.slaCategoryMetricCards.find((metric) => {
        const normalizedLabel = String(metric.label || "").toUpperCase();
        return labelTerms.some((term) => normalizedLabel.includes(term));
      });
    },
    defaultSlaMetricCard(label) {
      return {
        label,
        value: 0,
        icon: this.slaIcon(label),
        color: this.slaColor(label),
        btoCount: 0,
        loadingSource: "sla",
      };
    },
    glanceTone(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return "on-track";
      if (normalizedLabel.includes("MUST SHIP")) return "needs-attention";
      if (this.isNoTatMetric(label)) return "no-tat";
      return "at-risk";
    },
    glanceDescription(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return "Healthy and meeting commitments";
      if (normalizedLabel.includes("MUST SHIP")) return "Must ship soon and at risk if delayed";
      if (this.isNoTatMetric(label)) return "Missing TAT and needs review";
      return "Past due and requiring action";
    },
    applyDashboardMetricData(metricData, requestId, source = "") {
      const normalizedMetricData = this.normalizeDashboardMetricPayload(metricData);
      if (requestId !== this.dashboardMetricRequestId || !normalizedMetricData || normalizedMetricData?.message === "NA") return;
      if (source === "bto") {
        this.btoDashboardData = this.mapBtoDashboardData(normalizedMetricData);
        return;
      }
      if (source === "shipAudit") {
        this.shipAuditDashboardData = normalizedMetricData;
        return;
      }
      this.orderMatrixData = this.mergeDashboardMetricData(this.orderMatrixData, normalizedMetricData, source);
    },
    applyDashboardTrendData(metricData) {
      const normalizedMetricData = this.normalizeDashboardMetricPayload(metricData);
      if (!normalizedMetricData || normalizedMetricData?.message === "NA") return;
      const trendData = this.pickDashboardTrendFields(normalizedMetricData);
      if (!Object.keys(trendData).length) return;
      this.orderMatrixData = {
        ...this.orderMatrixData,
        ...trendData,
      };
    },
    pickDashboardTrendFields(source) {
      const trendKeys = [
        "orderDroppedTrend",
        "OrderDroppedTrend",
        "ordersDroppedTrend",
        "OrdersDroppedTrend",
        "trend",
        "Trend",
        "dailyTrend",
        "DailyTrend",
      ];
      const nestedTrendRows = this.firstArray(source, trendKeys, ["date", "Date", "orderDate"], false);
      return trendKeys.reduce((trendData, key) => {
        if (source?.[key] !== undefined) trendData[key] = source[key];
        return trendData;
      }, nestedTrendRows.length ? { orderDroppedTrend: nestedTrendRows } : {});
    },
    mergeDashboardMetricData(currentData, nextData, source = "") {
      if (!nextData || typeof nextData !== "object" || Array.isArray(nextData)) return currentData;
      const sourceData = this.mapSourceSpecificDashboardData(nextData, source);
      const sanitizedNextData = source === "matrix"
        ? this.withoutChunkOwnedDashboardSections(currentData, sourceData)
        : sourceData;
      const mergedData = {
        ...currentData,
        ...sanitizedNextData,
      };
      const summary = this.mergeDashboardSummary(currentData, sanitizedNextData, ["summary", "Summary", "dashboardSummary", "DashboardSummary"]);
      if (summary) {
        mergedData.summary = summary;
        mergedData.Summary = summary;
        mergedData.dashboardSummary = summary;
        mergedData.DashboardSummary = summary;
      }
      return mergedData;
    },
    mapSourceSpecificDashboardData(nextData, source = "") {
      return source === "bto" ? this.mapBtoDashboardData(nextData) : nextData;
    },
    mapBtoDashboardData(nextData) {
      const btoStatusBreakdown = this.firstArray(nextData, ["statusBreakdown", "StatusBreakdown"], ["status", "Status", "totalBTOs", "TotalBTOs"], false);
      const mappedData = { ...nextData };
      const btoSummary = this.pickDashboardSummaryFields(nextData, this.btoSummaryKeys());
      if (btoStatusBreakdown.length) {
        delete mappedData.statusBreakdown;
        delete mappedData.StatusBreakdown;
      }
      if (Object.keys(btoSummary).length) {
        mappedData.summary = {
          ...(this.firstDefined([
            nextData.summary,
            nextData.Summary,
            nextData.dashboardSummary,
            nextData.DashboardSummary,
          ]) || {}),
          ...btoSummary,
        };
      }
      return {
        ...mappedData,
        ...(btoStatusBreakdown.length ? {
          btoStatusBreakdown,
          BtoStatusBreakdown: btoStatusBreakdown,
          BTOStatusBreakdown: btoStatusBreakdown,
        } : {}),
      };
    },
    normalizeDashboardMetricPayload(metricData) {
      if (!metricData || typeof metricData !== "object") return metricData;
      const nestedMessage = this.firstDefined([
        metricData?.body?.data?.message,
        metricData?.body?.message,
        metricData?.data?.body?.data?.message,
        metricData?.data?.body?.message,
        metricData?.message,
      ]);
      if (typeof nestedMessage === "string" && nestedMessage.trim()) {
        try {
          return JSON.parse(nestedMessage);
        } catch (err) {
          return metricData;
        }
      }
      return metricData;
    },
    withoutChunkOwnedDashboardSections(currentData, nextData) {
      const sanitizedData = { ...nextData };
      const chunkOwnedKeys = [
        "orderStatusBreakdown",
        "OrderStatusBreakdown",
        "statusBreakdown",
        "StatusBreakdown",
        "openOrderStatus",
        "slaCategories",
        "SlaCategories",
        "SLACategories",
        "orderSlaSummary",
        "OrderSlaSummary",
        "slaSummary",
        "SlaSummary",
        "SLASummary",
      ];
      chunkOwnedKeys.forEach((key) => {
        if (currentData?.[key] !== undefined) delete sanitizedData[key];
      });
      this.removeExistingSummaryFieldGroups(sanitizedData, currentData, [
        ["totalBTOs", "TotalBTOs", "totalBtos", "TotalBtos", "todayBTOs", "TodayBTOs", "todayBtos", "TodayBtos"],
        ["totalClosed", "TotalClosed"],
        ["totalCanceled", "TotalCanceled", "totalCancelled", "TotalCancelled"],
      ]);
      return sanitizedData;
    },
    removeExistingSummaryFieldGroups(nextData, currentData, keyGroups) {
      const currentSummary = this.firstDefined([
        currentData?.summary,
        currentData?.Summary,
        currentData?.dashboardSummary,
        currentData?.DashboardSummary,
      ]) || {};
      const nextSummaryKeys = ["summary", "Summary", "dashboardSummary", "DashboardSummary"];
      nextSummaryKeys.forEach((summaryKey) => {
        if (!nextData?.[summaryKey]) return;
        keyGroups.forEach((keys) => {
          const currentHasGroupValue = keys.some((key) => currentSummary?.[key] !== undefined);
          if (currentHasGroupValue) keys.forEach((key) => delete nextData[summaryKey][key]);
        });
      });
    },
    mergeDashboardSummary(currentData, nextData, keys) {
      const currentSummary = this.firstDefined(keys.map((key) => currentData?.[key])) || {};
      const nextSummary = this.firstDefined(keys.map((key) => nextData?.[key])) || {};
      if (!Object.keys(currentSummary).length && !Object.keys(nextSummary).length) return null;
      return {
        ...currentSummary,
        ...nextSummary,
      };
    },
    pickDashboardSummaryFields(source, keys) {
      return keys.reduce((summary, key) => {
        const value = source?.[key];
        if (value !== undefined && value !== null) summary[key] = value;
        return summary;
      }, {});
    },
    btoSummaryKeys() {
      return [
        "totalBTOs",
        "TotalBTOs",
        "totalBtos",
        "TotalBtos",
        "todayBTOs",
        "TodayBTOs",
        "todayBtos",
        "TodayBtos",
        "totalClosed",
        "TotalClosed",
        "totalCanceled",
        "TotalCanceled",
        "totalCancelled",
        "TotalCancelled",
        "asOfDate",
        "AsOfDate",
        "generatedDate",
        "GeneratedDate",
      ];
    },
    metricValue(source, aliases) {
      return this.normalizeNumber(this.firstDefined(aliases.map((key) => source?.[key])));
    },
    directionIcon(direction) {
      if (direction === "NEW" || direction === "UP") return "mdi-trending-up";
      if (direction === "DOWN") return "mdi-trending-down";
      return "mdi-trending-neutral";
    },
    directionLabel(direction, percentChange = 0) {
      const percentLabel = this.formatChangePercent(percentChange);
      if (direction === "NEW" || direction === "UP") return `Up ${percentLabel}`;
      if (direction === "DOWN") return `Down ${percentLabel}`;
      return `No Change ${percentLabel}`;
    },
    directionClass(direction) {
      if (direction === "NEW") return "up";
      return String(direction || "NO CHANGE").toLowerCase().replace(" ", "-");
    },
    formatChangePercent(value) {
      const percentValue = Math.abs(this.normalizeNumber(value));
      if (percentValue > 0 && percentValue < 0.1) return `${percentValue.toFixed(2)}%`;
      return `${percentValue.toFixed(1)}%`;
    },
    formatTotalOrderChangePercent(value) {
      const percentValue = Math.abs(this.normalizeNumber(value));
      if (percentValue > 0 && percentValue < 0.1) return `${percentValue.toFixed(2)}%`;
      return `${percentValue.toFixed(1)}%`;
    },
    toProperCase(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    normalizeNumber(value) {
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    normalizeBreakdown(items, labelAliases, valueAliases, percentAliases = [], btoCountAliases = []) {
      return items.map((item) => ({
        label: this.firstDefined(labelAliases.map((key) => item?.[key])) || "",
        value: this.normalizeNumber(this.firstDefined(valueAliases.map((key) => item?.[key]))),
        percent: this.normalizeNumber(this.firstDefined(percentAliases.map((key) => item?.[key]))),
        btoCount: this.normalizeNumber(this.firstDefined(btoCountAliases.map((key) => item?.[key]))),
      })).filter((item) => item.label);
    },
    formatNumber(value) {
      return this.normalizeNumber(value).toLocaleString();
    },
    formatPercent(value, total) {
      if (!total) return "0.0%";
      return `${((this.normalizeNumber(value) / total) * 100).toFixed(1)}%`;
    },
    formatBreakdownPercent(item, total) {
      return item.percent ? `${item.percent.toFixed(1)}%` : this.formatPercent(item.value, total);
    },
    formatDashboardDate(value) {
      return value ? moment(value).format("MMM D") : "";
    },
    colorForIndex(index) {
      const colors = ["#2563eb", DASHBOARD_GOOD_GREEN, "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#64748b", "#ec4899", "#14b8a6", "#a855f7"];
      return colors[index % colors.length];
    },
    slaColor(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return DASHBOARD_GOOD_GREEN;
      if (normalizedLabel.includes("MUST SHIP")) return "#c97a00";
      if (normalizedLabel.includes("PAST DUE") || normalizedLabel.includes("PAST-DUE")) return "#d84315";
      if (this.isNoTatMetric(label)) return "#b0bec5";
      return "#64748b";
    },
    slaIcon(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      if (normalizedLabel.includes("ON-TIME") || normalizedLabel.includes("ON TIME")) return "mdi-check-circle-outline";
      if (normalizedLabel.includes("MUST SHIP")) return "mdi-alert-circle-outline";
      if (normalizedLabel.includes("PAST DUE") || normalizedLabel.includes("PAST-DUE")) return "mdi-clock-alert-outline";
      if (this.isNoTatMetric(label)) return "mdi-timer-off-outline";
      return "mdi-chart-donut";
    },
    isNoTatMetric(label) {
      const compactLabel = String(label || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      return compactLabel.includes("NOTAT");
    },
    isBtoSlaMetric(label) {
      const normalizedLabel = String(label || "").toUpperCase();
      return normalizedLabel.includes("ON-TIME") ||
        normalizedLabel.includes("ON TIME") ||
        normalizedLabel.includes("MUST SHIP") ||
        normalizedLabel.includes("PAST DUE") ||
        normalizedLabel.includes("PAST-DUE") ||
        normalizedLabel.includes("PART DUE") ||
        this.isNoTatMetric(label);
    },
    normalizeStatusLabel(label) {
      return String(label || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
    },
    statusBarColor(label, index) {
      const normalizedLabel = this.normalizeStatusLabel(label);
      if (normalizedLabel.includes("BACKORDER")) return "#dc2626";
      if (normalizedLabel.includes("READY TO PICK")) return DASHBOARD_GOOD_GREEN;
      if (normalizedLabel.includes("HOLD") || normalizedLabel.includes("HELD")) return "#f59e0b";

      const colors = ["#4f8f3a", "#f2c23e", "#d95f38", "#7c4dd8", "#5db8ad", "#e5b744", "#6bb7b5", "#d95a83", "#8b949e"];
      return colors[index % colors.length];
    },
    shipmentAuditStatusColor(label, index) {
      const normalizedLabel = this.normalizeStatusLabel(label);
      if (normalizedLabel.includes("PROGRESS")) return "#2563eb";
      if (normalizedLabel.includes("PENDING")) return "#f59e0b";
      if (normalizedLabel.includes("RE AUDIT") || normalizedLabel.includes("REAUDIT")) return "#dc2626";
      if (normalizedLabel.includes("COMPLETE") || normalizedLabel.includes("AUDITED")) return DASHBOARD_GOOD_GREEN;
      return this.colorForIndex(index);
    },
    shipmentAuditStatusIcon(label) {
      const normalizedLabel = this.normalizeStatusLabel(label);
      if (normalizedLabel.includes("PROGRESS")) return "mdi-refresh";
      if (normalizedLabel.includes("PENDING")) return "mdi-clock-outline";
      if (normalizedLabel.includes("RE AUDIT") || normalizedLabel.includes("REAUDIT")) return "mdi-sync-alert";
      if (normalizedLabel.includes("COMPLETE") || normalizedLabel.includes("AUDITED")) return "mdi-check-circle-outline";
      return "mdi-clipboard-pulse-outline";
    },
    shipmentAuditStatusDescription(label) {
      const normalizedLabel = this.normalizeStatusLabel(label);
      if (normalizedLabel.includes("PROGRESS")) return "Currently being audited";
      if (normalizedLabel.includes("PENDING")) return "Waiting to be audited";
      if (normalizedLabel.includes("RE AUDIT") || normalizedLabel.includes("REAUDIT")) return "Requires re-audit";
      if (normalizedLabel.includes("COMPLETE") || normalizedLabel.includes("AUDITED")) return "Completed audit work";
      return "Audit items by status";
    },
    formatShipmentAuditValue(card) {
      return card?.isPercent ? `${this.normalizeNumber(card.value).toFixed(1)}%` : this.formatNumber(card?.value);
    },
    formatShipmentAuditPercent(value) {
      return `${this.normalizeNumber(value).toFixed(1)}%`;
    },
    btoStatusBarWidth(item) {
      const max = Math.max(...this.btoStatusBreakdown.map((status) => this.normalizeNumber(status.value)), 1);
      return Math.max((this.normalizeNumber(item.value) / max) * 100, item.value ? 3 : 0);
    },
    statusBarWidth(item) {
      return Math.max((this.normalizeNumber(item.value) / this.statusBreakdownMax) * 100, item.value ? 3 : 0);
    },
    selectBreakdownItem(title, item, total, index) {
      this.dashboardInsight = {
        type: "breakdown",
        title,
        label: item.label,
        value: this.formatNumber(item.value),
        detail: this.formatBreakdownPercent(item, total),
        color: item.color || this.statusBarColor(item.label, index),
      };
    },
    selectTrendPoint(point, series = "orders") {
      this.hoveredTrendIndex = point.index;
      this.hoveredTrendSeries = series;
      const isAverage = series === "average";
      const isShippingAverage = series === "shippingAverage";
      const isShipped = series === "shipped";
      this.dashboardInsight = {
        type: "trend",
        series,
        title: isShippingAverage ? "Shipping Average" : isAverage ? "Rolling Average" : isShipped ? "Orders Shipped" : "Orders Dropped",
        label: this.formatDashboardDate(point.date),
        value: this.formatNumber(isShippingAverage ? point.rollingShippingAverage : isAverage ? point.dailyAverage : isShipped ? point.ordersShipped : point.ordersDropped),
        detail: isAverage || isShippingAverage ? "Trend line" : "Bar value",
        color: isShippingAverage || isShipped ? "#16a34a" : "#2563eb",
      };
    },
    clearTrendHover() {
      this.hoveredTrendIndex = null;
      this.hoveredTrendSeries = null;
    },
    clearDashboardInsight() {
      this.dashboardInsight = null;
    },
    donutSegments(items, total) {
      if (!total) return [];
      let cursor = 0;
      return items.map((item, index) => {
        const portion = item.value / total;
        const start = cursor;
        cursor += portion;
        return {
          item,
          index,
          color: item.color || this.colorForIndex(index),
          path: this.donutArcPath(90, 90, 70, start * 359.99, cursor * 359.99),
        };
      });
    },
    donutArcPath(cx, cy, radius, startAngle, endAngle) {
      const start = this.polarToCartesian(cx, cy, radius, endAngle);
      const end = this.polarToCartesian(cx, cy, radius, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
    },
    polarToCartesian(cx, cy, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
      return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
      };
    },
    donutStyle(items) {
      const colors = ["#2563eb", DASHBOARD_GOOD_GREEN, "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#64748b"];
      const total = items.reduce((sum, item) => sum + item.value, 0);
      if (!total) return { background: "#e5e7eb" };
      let cursor = 0;
      const stops = items.map((item, index) => {
        const start = cursor;
        cursor += (item.value / total) * 100;
        return `${colors[index % colors.length]} ${start}% ${cursor}%`;
      });
      return { background: `conic-gradient(${stops.join(", ")})` };
    },
    buildTrendPoints(rows, valueKey) {
      if (!rows.length) return [];
      const max = this.trendScaleMax;
      const chartWidth = TREND_CHART.right - TREND_CHART.left;
      const chartHeight = TREND_CHART.bottom - TREND_CHART.top;
      const slotWidth = chartWidth / rows.length;
      return rows.map((row, index) => ({
        x: TREND_CHART.left + slotWidth * index + slotWidth / 2,
        y: TREND_CHART.bottom - (row[valueKey] / max) * chartHeight,
        value: row[valueKey],
        date: row.date,
        ordersDropped: row.ordersDropped,
        dailyAverage: row.dailyAverage,
        ordersShipped: row.ordersShipped,
        rollingShippingAverage: row.rollingShippingAverage,
        index,
      }));
    },
    firstArray(source, keys, itemKeys = [], allowDirectArray = true) {
      if (Array.isArray(source)) {
        if (allowDirectArray || this.arrayMatchesItemKeys(source, itemKeys)) return source;
        return [];
      }
      for (const key of keys) {
        if (Array.isArray(source?.[key])) return source[key];
      }
      if (source && typeof source === "object") {
        for (const [key, value] of Object.entries(source)) {
          if (keys.includes(key) && Array.isArray(value)) return value;
          if (Array.isArray(value) && this.arrayMatchesItemKeys(value, itemKeys)) return value;
          const nestedArray = this.firstArray(value, keys, itemKeys, false);
          if (nestedArray.length) return nestedArray;
        }
      }
      return [];
    },
    arrayMatchesItemKeys(items, itemKeys) {
      return itemKeys.length > 0 && items.some((item) => (
        item &&
        typeof item === "object" &&
        itemKeys.some((key) => item[key] !== undefined && item[key] !== null)
      ));
    },
    toSelectItems(items) {
      return items.map((item) => {
        if (typeof item !== "object" || item === null) {
          return { text: item, value: this.normalizeId(item) };
        }
        const text =
          this.firstDefined([
            item.Project,
            item.ProjectName,
            item.projectName,
            item.project,
            item.project_name,
            item.Business,
            item.BusinessName,
            item.businessUnit,
            item.BUName,
            item.business,
            item.business_name,
            item.bu_name,
            item.BU,
            item.bu,
            item.Facility,
            item.FacilityName,
            item.facilityName,
            item.facility,
            item.facility_name,
            item.Name,
            item.name,
            item.Text,
            item.text,
            item.Description,
            item.description,
            item.Value,
            item.value,
          ]);
        const value =
          this.firstDefined([
            item.ProjKey,
            item.Proj_Key,
            item.projectID,
            item.projectId,
            item.proj_key,
            item.proj_id,
            item.ProjectId,
            item.project_id,
            item.ProjectKey,
            item.project_key,
            item.BuKey,
            item.BuId,
            item.businessUnitID,
            item.bu_key,
            item.bu_id,
            item.business_id,
            item.Bu_id,
            item.FacilityKey,
            item.FacilityID,
            item.FacilityId,
            item.facilityID,
            item.facilityId,
            item.facilityid,
            item.facility_key,
            item.facility_id,
            item.LocaKey,
            item.loca_key,
            item.Value,
            item.value,
            text,
          ]);
        return { text, value: this.normalizeId(value) };
      }).filter((item) => item.text !== undefined && item.value !== undefined);
    },
    firstDefined(values) {
      return values.find((value) => value !== undefined && value !== null);
    },
    normalizeId(value) {
      const numericValue = Number(value);
      return Number.isInteger(numericValue) ? numericValue : value;
    },
    normalizeSelectedIds(values) {
      return (values || []).map((value) => this.normalizeId(value)).filter((value) => Number.isInteger(value));
    },
  },
};
